import { Tag, Tooltip, Typography } from 'antd';
import { DataTableColumnHeader } from 'common/data-table-column-header';
import { FilterFieldConfig } from 'common/dynamic-filter-form';
import { ExternalLink } from 'lucide-react';
import { dateTimeCell, linkCell } from 'utils/cell-formatter';
import {
  getReportResultUrl,
  surveyReportStatuses,
  surveyReportTypeOptions,
  surveyReportTypes,
} from './constants';

export const LookupTag = ({ map, value }) => {
  const item = map[value];
  if (!item) return <Tag>{value ?? '-'}</Tag>;
  return <Tag color={item.color}>{item.label}</Tag>;
};

const getMetadataValue = (metadata: string, key: string) => {
  if (!metadata) return null;
  try {
    return JSON.parse(metadata).find(item => item.key === key)?.value || null;
  } catch {
    return null;
  }
};

export const columnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) => linkCell({ getValue, path: '/hub/survey-reports' }),
  },
  {
    accessorKey: 'user_name',
    header: 'User',
    cell: ({ row }) => (
      <div>
        <div>{row.original.user_name || '-'}</div>
        <div className="text-xs text-muted-foreground">
          {row.original.email}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'company_name',
    header: 'Company',
    cell: ({ getValue }) => getValue() || '-',
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ getValue }) => (
      <LookupTag map={surveyReportTypes} value={getValue()} />
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => (
      <LookupTag map={surveyReportStatuses} value={getValue()} />
    ),
  },
  {
    accessorKey: 'code',
    header: 'Report Code',
    cell: ({ getValue }) => {
      const code = getValue();
      if (!code) return '-';
      return (
        <Typography.Text
          copyable={{ text: code }}
          code
          className="whitespace-nowrap"
        >
          {code.slice(0, 8)}
        </Typography.Text>
      );
    },
  },
  {
    id: 'result',
    header: 'Result',
    cell: ({ row }) => {
      const url = getReportResultUrl(row.original);
      if (!url) return null;
      return (
        <Tooltip title="Open health check result">
          <a href={url} target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4" />
          </a>
        </Tooltip>
      );
    },
  },
  {
    id: 'utm',
    header: 'UTM',
    cell: ({ row }) => {
      const source = getMetadataValue(row.original.metadata, 'utm_source');
      const medium = getMetadataValue(row.original.metadata, 'utm_medium');
      if (!source && !medium) return '-';
      return (
        <span className="whitespace-nowrap text-muted-foreground">
          {source || '-'} / {medium || '-'}
        </span>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
];

export const filterConfig: FilterFieldConfig[] = [
  {
    name: 'type',
    type: 'select',
    label: 'Type',
    placeholder: 'All',
    minWidth: '180px',
    options: surveyReportTypeOptions,
    defaultValue: 'all',
  },
];
